import React, { useRef } from 'react';
import { 
  Box, 
  Typography, 
  Button 
} from '@mui/material';
import { Sync, AutoFixHigh, PhotoCamera } from '@mui/icons-material';
import ComparisonView from '../../../components/ComparisonView.js';
import ErrorSuccessAlerts from './ErrorSuccessAlerts.js';

const ImageImportTab = ({
  isProcessing,
  handleImageUpload,
  handleImageSubmit,
  showComparison,
  parsedLines,
  inventory,
  setRecipeData,
  setShowComparison,
  imageFile,
  imagePreview,
  error,
  success
}) => {
  const fileInputRef = useRef(null);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="body1" gutterBottom>
        Take a picture of a recipe (book, card, etc.) and we'll extract the content.
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', my: 2 }}>
        <input
          accept="image/*"
          style={{ display: 'none' }}
          id="image-upload"
          type="file"
          onChange={handleImageUpload}
          ref={fileInputRef}
          capture="environment"
        />
        <label htmlFor="image-upload">
          <Button
            variant="outlined"
            component="span"
            startIcon={<PhotoCamera />}
            sx={{ mb: 2 }}
          >
            Take Photo or Upload Image
          </Button>
        </label>

        {imagePreview && (
          <Box sx={{ mb: 2, mt: 2, textAlign: 'center' }}>
            <img
              src={imagePreview}
              alt="Recipe"
              style={{
                maxWidth: '100%',
                maxHeight: '300px',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}
            />
          </Box>
        )}

        <Button
          variant="contained"
          onClick={handleImageSubmit}
          disabled={!imageFile || isProcessing}
          startIcon={isProcessing ? <Sync sx={{ animation: 'spin 1s linear infinite' }} /> : <AutoFixHigh />}
          sx={{ mt: 2 }}
        >
          {isProcessing ? 'Processing...' : 'Extract Recipe from Image'}
        </Button>
      </Box>

      {showComparison && parsedLines && parsedLines.length > 0 ? (
        <ComparisonView
          parsedLines={parsedLines}
          inventory={inventory || []}
          onAccept={(editedLines) => {
            const newRecipeData = {
              name: editedLines.find(l => l.type === 'name')?.parsed.value || '',
              origin: 'My Recipes',
              description: editedLines
                .filter(l => l.type === 'description')
                .map(l => l.parsed.value)
                .join('\n'),
              ingredients: editedLines
                .filter(l => l.type === 'ingredient')
                .map(l => ({
                  name: l.parsed.name,
                  amount: l.parsed.amount,
                  unit: l.parsed.unit,
                  ingredientId: inventory.find(i =>
                    i.name.toLowerCase() === l.parsed.name.toLowerCase()
                  )?.id || ''
                })),
              instructions: editedLines
                .filter(l => l.type === 'instruction')
                .map(l => l.parsed.value)
                .join('\n'),
              garnish: editedLines
                .filter(l => l.type === 'garnish')
                .map(l => l.parsed.value)
                .join(', '),
              glass: editedLines
                .filter(l => l.type === 'glass')
                .map(l => l.parsed.value)
                .join(', ')
            };
            setRecipeData(newRecipeData);
            setShowComparison(false);
          }}
          onEdit={(editedLines) => {
            const newRecipeData = {
              name: editedLines.find(l => l.type === 'name')?.parsed.value || '',
              origin: 'My Recipes',
              description: editedLines
                .filter(l => l.type === 'description')
                .map(l => l.parsed.value)
                .join('\n'),
              ingredients: editedLines
                .filter(l => l.type === 'ingredient')
                .map(l => ({
                  name: l.parsed.name,
                  amount: l.parsed.amount,
                  unit: l.parsed.unit,
                  ingredientId: inventory.find(i =>
                    i.name.toLowerCase() === l.parsed.name.toLowerCase()
                  )?.id || ''
                })),
              instructions: editedLines
                .filter(l => l.type === 'instruction')
                .map(l => l.parsed.value)
                .join('\n'),
              garnish: editedLines
                .filter(l => l.type === 'garnish')
                .map(l => l.parsed.value)
                .join(', '),
              glass: editedLines
                .filter(l => l.type === 'glass')
                .map(l => l.parsed.value)
                .join(', ')
            };
            setRecipeData(newRecipeData);
            setShowComparison(false);
          }}
        />
      ) : showComparison && (
        <Typography variant="body2" sx={{ color: 'warning.main', mt: 2 }}>
          No recipe content was extracted. Please try again with a clearer image.
        </Typography>
      )}

      <ErrorSuccessAlerts error={error} success={success} />
    </Box>
  );
};

export default ImageImportTab; 